import { useState } from 'react';
import { ServiceTemplate, PaginatedResponse } from '../types/entities';
import { ServiceTemplateFormData } from '../types/forms';
import { getAuthenticatedFetch } from '../utils/api';
import { useErrorHandler } from '../utils/errorHandler';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface ApiError {
  message: string;
  status?: number;
}

export const useServiceTemplatesApi = () => {
  const [error, setError] = useState<ApiError | null>(null);
  const { handleApiError: handleError } = useErrorHandler();

  const handleApiError = (err: any): ApiError => {
    const apiError = handleError(err);
    setError(apiError);
    return apiError;
  };

  const clearError = () => setError(null);

  const fetchServiceTemplates = async (params?: { page?: number; limit?: number }): Promise<PaginatedResponse<ServiceTemplate>> => {
    try {
      clearError();
      
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.append('page', params.page.toString());
      if (params?.limit) searchParams.append('limit', params.limit.toString());
      
      const queryString = searchParams.toString();
      const url = queryString ? `/service-templates?${queryString}` : `/service-templates`;
      const authenticatedFetch = getAuthenticatedFetch();
      
      const response = await authenticatedFetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data: PaginatedResponse<ServiceTemplate> = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  };

  const createServiceTemplate = async (serviceTemplateData: ServiceTemplateFormData): Promise<ServiceTemplate> => {
    try {
      clearError();
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/service-templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(serviceTemplateData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  };

  const updateServiceTemplate = async (id: number, serviceTemplateData: Partial<ServiceTemplateFormData>): Promise<ServiceTemplate> => {
    try {
      clearError();
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/service-templates/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(serviceTemplateData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  };

  const deleteServiceTemplate = async (id: number): Promise<void> => {
    try {
      clearError();
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/service-templates/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (err) {
      throw handleApiError(err);
    }
  };

  return {
    error,
    clearError,
    fetchServiceTemplates,
    createServiceTemplate,
    updateServiceTemplate,
    deleteServiceTemplate,
  };
};

